import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  financeApi,
  type CreateInvoicePayload,
  type CreateReglementPayload,
  type InvoiceQuery,
} from '@/api/finance';

const INVOICES_KEY = ['finance', 'invoices'] as const;
// useContacts vit dans hooks/useCrm.ts (point de vérité unique CRM)
export { useContacts } from './useCrm';

export const useInvoices = (query: InvoiceQuery = {}) =>
  useQuery({
    queryKey:  [...INVOICES_KEY, query],
    queryFn:   () => financeApi.getInvoices(query),
    staleTime: 30_000,
  });

export const useInvoice = (id: string | undefined) =>
  useQuery({
    queryKey: [...INVOICES_KEY, id],
    queryFn:  () => financeApi.getInvoice(id!),
    enabled:  !!id,
  });

/**
 * Helper : invalide tous les dashboards impactés par une mutation invoice.
 * Cross-tenant : le KPI Holding agrège tous les tenants, donc une validation
 * dans une filiale doit rafraîchir aussi le dashboard Holding du PDG.
 */
const invalidateDashboards = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: INVOICES_KEY });
  qc.invalidateQueries({ queryKey: ['finance', 'kpis'] });
  qc.invalidateQueries({ queryKey: ['finance', 'journal'] });
  qc.invalidateQueries({ queryKey: ['finance', 'balance-agee'] });
  qc.invalidateQueries({ queryKey: ['holding', 'kpis'] });
  // Bridge production : mutations peuvent créer commandes + OF
  qc.invalidateQueries({ queryKey: ['operations', 'commandes'] });
  qc.invalidateQueries({ queryKey: ['operations', 'ordres-fabrication'] });
  qc.invalidateQueries({ queryKey: ['operations', 'ordres-impression'] });
  qc.invalidateQueries({ queryKey: ['operations', 'dashboard-dg'] });
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInvoicePayload) => financeApi.createInvoice(payload),
    onSuccess: () => invalidateDashboards(qc),
  });
};

export const useUpdateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateInvoicePayload }) =>
      financeApi.updateInvoice(id, payload),
    onSuccess: (_, { id }) => {
      invalidateDashboards(qc);
      qc.invalidateQueries({ queryKey: [...INVOICES_KEY, id] });
    },
  });
};

export const useMarkInvoicePaid = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.markInvoicePaid(id),
    onSuccess: () => invalidateDashboards(qc),
  });
};

/**
 * Enregistre un encaissement client (total ou partiel) et rafraîchit tout ce
 * qui lit `paidAmountCents` : liste des factures, balance âgée, KPI.
 */
export const useEnregistrerReglement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateReglementPayload }) =>
      financeApi.enregistrerReglement(id, payload),
    onSuccess: (_data, variables) => {
      invalidateDashboards(qc);
      qc.invalidateQueries({ queryKey: ['finance', 'reglements', variables.id] });
    },
    // Délai dépassé : l'encaissement a peut-être été enregistré — on recharge pour montrer ce qui l'a réellement été.
    onError: (_err, variables) => {
      invalidateDashboards(qc);
      qc.invalidateQueries({ queryKey: ['finance', 'reglements', variables.id] });
    },
  });
};

/** Historique des encaissements d'une facture. */
export const useReglements = (id: string | undefined) =>
  useQuery({
    queryKey: ['finance', 'reglements', id],
    queryFn:  () => financeApi.getReglements(id!),
    enabled:  Boolean(id),
  });

export const useValidateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.validateInvoice(id),
    onSuccess: () => invalidateDashboards(qc),
  });
};

/** Ticket SIMGROUP-2026-001 (MANQUE-01) — annulation proforma/draft avec motif obligatoire */
export const useCancelInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      financeApi.cancelInvoice(id, motif),
    onSuccess: (_, { id }) => {
      invalidateDashboards(qc);
      qc.invalidateQueries({ queryKey: [...INVOICES_KEY, id] });
    },
  });
};

/**
 * Ticket SIMGROUP-2026-003 Sprint 2 — Télécharge le PDF OHADA de la facture.
 * Backend refuse 400 avec MISSING_LEGAL_EMITTER/MISSING_LEGAL_CLIENT si les
 * mentions obligatoires ne sont pas renseignées. On parse le blob d'erreur
 * pour extraire le message métier et lever une Error explicite pour l'UI.
 */
export const useDownloadInvoicePdf = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const { blob, filename } = await financeApi.downloadInvoicePdf(id);
        // Déclenche le download navigateur
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        return { filename };
      } catch (err: any) {
        // Le backend renvoie l'erreur en JSON même quand responseType='blob'
        // → on doit lire le blob et le parser
        const blob = err?.response?.data;
        if (blob instanceof Blob) {
          try {
            const text = await blob.text();
            const parsed = JSON.parse(text);
            const apiError = parsed?.error ?? { code: 'UNKNOWN', message: text };
            throw Object.assign(new Error(apiError.message), { code: apiError.code });
          } catch (parseErr) {
            if (parseErr instanceof Error && (parseErr as any).code) throw parseErr;
            // ignore parse error, throw original
          }
        }
        throw err;
      }
    },
  });
};
